"use client";

import * as React from "react";
import {
  AdminPortalDomainVerificationError,
  AdminPortalDomainVerificationLoading,
  AdminPortalDomainVerification as AdminPortalDomainVerificationPresentational,
} from "./lib/admin-portal-domain-verification.js";
import type {
  AdminPortalDomainVerificationErrorProps,
  AdminPortalDomainVerificationLoadingProps,
} from "./lib/admin-portal-domain-verification.js";
import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import {
  useListOrganizationDomains,
  getListOrganizationDomainsQueryKey,
  useDeleteOrganizationDomain,
  useReverifyOrganizationDomain,
  useGenerateAdminPortalLink,
} from "./api/endpoint.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import { useQueryClient } from "@tanstack/react-query";
import { openAdminPortalViaForm, WidgetRootDomProps } from "./lib/utils.js";

interface AdminPortalDomainVerificationProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const AdminPortalDomainVerification: React.FC<
  AdminPortalDomainVerificationProps
> = ({ authToken, ...domProps }) => {
  const baseUrl = useWorkOsApiUrl();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <AdminPortalDomainVerificationError error={error} {...domProps} />
      )}
    >
      <ApiProvider
        widgetType="admin-portal-domain-verification"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <AdminPortalDomainVerificationImpl {...domProps} />
      </ApiProvider>
    </ErrorBoundary>
  );
};

const AdminPortalDomainVerificationImpl = (
  props: Omit<AdminPortalDomainVerificationProps, "authToken">,
) => {
  const isApiReady = useApiReady();
  const queryClient = useQueryClient();
  const {
    data: organizationDomains,
    isLoading,
    error,
  } = useListOrganizationDomains();

  const { mutate: generateAdminPortalLink, isPending } =
    useGenerateAdminPortalLink({
      mutation: {
        onSuccess: (data) => {
          openAdminPortalViaForm(data.link, {
            target: "_blank",
            rel: "noopener noreferrer",
          });
        },
      },
    });

  const { mutate: deleteDomain } = useDeleteOrganizationDomain({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListOrganizationDomainsQueryKey(),
        });
      },
    },
  });

  const { mutate: reverifyDomain } = useReverifyOrganizationDomain({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListOrganizationDomainsQueryKey(),
        });
      },
    },
  });

  const handleAddDomain = () => {
    generateAdminPortalLink({
      params: {
        intent: "domain_verification",
      },
    });
  };

  const handleDeleteDomain = (domainId: string) => {
    deleteDomain({ domainId });
  };

  const handleReverifyDomain = (domainId: string) => {
    reverifyDomain({ domainId });
  };

  if (!isApiReady || isLoading) {
    return <AdminPortalDomainVerificationLoading {...props} />;
  }

  if (error) {
    return <AdminPortalDomainVerificationError error={error} {...props} />;
  }

  const domains = organizationDomains?.data || [];

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <AdminPortalDomainVerificationError error={error} {...props} />
      )}
    >
      <AdminPortalDomainVerificationPresentational
        organizationDomains={domains}
        onAddDomain={handleAddDomain}
        onDeleteDomain={handleDeleteDomain}
        onReverifyDomain={handleReverifyDomain}
        isPending={isPending}
        {...props}
      />
    </ErrorBoundary>
  );
};

export type {
  AdminPortalDomainVerificationProps,
  AdminPortalDomainVerificationLoadingProps,
  AdminPortalDomainVerificationErrorProps,
};
export {
  AdminPortalDomainVerification,
  AdminPortalDomainVerificationLoading,
  AdminPortalDomainVerificationError,
};
